import { useCallback, useRef, useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

// 静音状态的 localStorage key
const SOUND_MUTED_KEY = 'boyangcong-sound-muted';

// 获取初始静音状态
function getInitialMutedState(): boolean {
  if (typeof window === 'undefined') return false;
  const saved = localStorage.getItem(SOUND_MUTED_KEY);
  return saved === 'true';
}

// 全局静音状态（用于跨组件共享）
let globalMuted = getInitialMutedState();
const muteListeners: Set<(muted: boolean) => void> = new Set();

// 创建 AudioContext（延迟初始化以符合浏览器政策）
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
      return null;
    }
  }
  
  // 恢复被暂停的 AudioContext
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  
  return audioContext;
}

// 播放基础音调
function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType,
  volume: number,
  ctx: AudioContext
) {
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
  
  // 音量包络：快速起音，平滑衰减
  gainNode.gain.setValueAtTime(0, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}

// ========== 人族 Terran 音效 ==========

// 人族悬停音效：短促雷达扫描提示音
function playTerranHover(ctx: AudioContext) {
  const frequency = 800;
  const duration = 0.08;
  const volume = 0.15;
  
  playTone(frequency, duration, 'sine', volume, ctx);
  
  // 添加轻微的频率滑动效果
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency * 1.2, ctx.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.8, ctx.currentTime + duration);
  
  gainNode.gain.setValueAtTime(0, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(volume * 0.5, ctx.currentTime + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}

// 人族点击音效：金属确认音
function playTerranClick(ctx: AudioContext) {
  const frequency = 400;
  const duration = 0.12;
  const volume = 0.2;
  
  // 主音调 - 低频方波
  playTone(frequency, duration, 'square', volume, ctx);
  
  // 泛音 - 高频补充
  playTone(1200, duration * 0.8, 'sine', 0.08, ctx);
  
  // 添加金属碰撞感的噪声
  const bufferSize = ctx.sampleRate * duration;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    output[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.1));
  }
  
  const noiseNode = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const noiseFilter = ctx.createBiquadFilter();
  
  noiseNode.buffer = noiseBuffer;
  noiseFilter.type = 'highpass';
  noiseFilter.frequency.value = 2000;
  noiseGain.gain.value = 0.05;
  
  noiseNode.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  
  noiseNode.start(ctx.currentTime);
}

// ========== 虫族 Zerg 音效 ==========

// 虫族悬停音效：有机嘶嘶声
function playZergHover(ctx: AudioContext) {
  const duration = 0.1;
  const volume = 0.12;
  
  // 低频嗡鸣
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sawtooth';
  osc1.frequency.setValueAtTime(120, ctx.currentTime);
  osc1.frequency.linearRampToValueAtTime(80, ctx.currentTime + duration);
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
  
  // 嘶嘶声（滤波噪声）
  const bufferSize = ctx.sampleRate * duration;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    output[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.3));
  }
  
  const noiseNode = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const noiseFilter = ctx.createBiquadFilter();
  
  noiseNode.buffer = noiseBuffer;
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 3000;
  noiseFilter.Q.value = 2;
  noiseGain.gain.value = volume * 0.4;
  
  noiseNode.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noiseNode.start(ctx.currentTime);
}

// 虫族点击音效：粘液/有机撕裂音
function playZergClick(ctx: AudioContext) {
  const duration = 0.18;
  const volume = 0.18;
  
  // 低频咆哮
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sawtooth';
  osc1.frequency.setValueAtTime(60, ctx.currentTime);
  osc1.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + duration);
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.02);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
  
  // 中频有机音
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'triangle';
  osc2.frequency.setValueAtTime(200, ctx.currentTime);
  osc2.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + duration * 0.5);
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.linearRampToValueAtTime(volume * 0.6, ctx.currentTime + 0.01);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration * 0.7);
  
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
  
  // 粘液噪声
  const bufferSize = ctx.sampleRate * duration;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    // 不规则噪声模拟粘液
    const phase = i / ctx.sampleRate;
    output[i] = (Math.random() * 2 - 1) * 
                Math.sin(phase * 50) * 
                Math.exp(-i / (bufferSize * 0.2));
  }
  
  const noiseNode = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const noiseFilter = ctx.createBiquadFilter();
  
  noiseNode.buffer = noiseBuffer;
  noiseFilter.type = 'lowpass';
  noiseFilter.frequency.value = 2000;
  noiseGain.gain.value = volume * 0.5;
  
  noiseNode.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noiseNode.start(ctx.currentTime);
}

// ========== 神族 Protoss 音效 ==========

// 神族悬停音效：空灵水晶共鸣
function playProtossHover(ctx: AudioContext) {
  const duration = 0.1;
  const volume = 0.12;
  
  // 主音 - 高频水晶音
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(1200, ctx.currentTime);
  osc1.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + duration);
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.005);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
  
  // 谐波 - 空灵泛音
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(2400, ctx.currentTime);
  osc2.frequency.exponentialRampToValueAtTime(1600, ctx.currentTime + duration);
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.linearRampToValueAtTime(volume * 0.3, ctx.currentTime + 0.005);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
}

// 神族点击音效：灵能水晶激活
function playProtossClick(ctx: AudioContext) {
  const duration = 0.15;
  const volume = 0.18;
  
  // 主音 - 灵能脉冲（频率上升）
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(400, ctx.currentTime);
  osc1.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + duration * 0.3);
  osc1.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + duration);
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
  gain1.gain.setValueAtTime(volume * 0.8, ctx.currentTime + duration * 0.3);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
  
  // 水晶共鸣泛音
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(1200, ctx.currentTime);
  osc2.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + duration * 0.5);
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.linearRampToValueAtTime(volume * 0.4, ctx.currentTime + 0.02);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
  
  // 高频闪烁（模拟能量激活）
  const osc3 = ctx.createOscillator();
  const gain3 = ctx.createGain();
  osc3.type = 'triangle';
  osc3.frequency.setValueAtTime(3000, ctx.currentTime);
  
  gain3.gain.setValueAtTime(0, ctx.currentTime);
  gain3.gain.linearRampToValueAtTime(volume * 0.15, ctx.currentTime + 0.005);
  gain3.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration * 0.5);
  
  osc3.connect(gain3);
  gain3.connect(ctx.destination);
  osc3.start(ctx.currentTime);
  osc3.stop(ctx.currentTime + duration * 0.5);
}

// ========== 赛博朋克 Cyber 音效 ==========

// 赛博悬停音效：数据扫描音 - 快速高低音交替，模拟扫描条纹
function playCyberHover(ctx: AudioContext) {
  const duration = 0.1;
  const volume = 0.12;
  
  // 快速交替的方波扫描音（模拟数据扫描条纹）
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'square';
  // 快速频率跳变 1000→500→1000→500
  osc1.frequency.setValueAtTime(1000, ctx.currentTime);
  osc1.frequency.setValueAtTime(500, ctx.currentTime + 0.025);
  osc1.frequency.setValueAtTime(1000, ctx.currentTime + 0.05);
  osc1.frequency.setValueAtTime(500, ctx.currentTime + 0.075);
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.005);
  gain1.gain.setValueAtTime(volume, ctx.currentTime + duration * 0.8);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  // 位压碎失真效果
  const distortion = ctx.createWaveShaper();
  const curve = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const x = (i / 128) - 1;
    // 更强的失真曲线，模拟数字化
    curve[i] = Math.sign(x) * Math.pow(Math.abs(x), 0.5);
  }
  distortion.curve = curve;
  
  osc1.connect(distortion);
  distortion.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
}

// 赛博点击音效：电子故障/Glitch音 - 数字噪声脉冲 + 合成器钉音
function playCyberClick(ctx: AudioContext) {
  const duration = 0.12;
  const volume = 0.18;
  
  // 白噪声脉冲（数字故障感）
  const bufferSize = ctx.sampleRate * 0.05;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    // 数字化噪声：量化效果
    const noise = Math.random() * 2 - 1;
    output[i] = Math.round(noise * 4) / 4 * Math.exp(-i / (bufferSize * 0.3));
  }
  
  const noiseNode = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const noiseFilter = ctx.createBiquadFilter();
  
  noiseNode.buffer = noiseBuffer;
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 2000;
  noiseFilter.Q.value = 1;
  noiseGain.gain.value = volume * 0.6;
  
  noiseNode.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noiseNode.start(ctx.currentTime);
  
  // 合成器钉音（尖锐的数字确认音）
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sawtooth';
  // 快速频率跳变模拟glitch
  osc1.frequency.setValueAtTime(800, ctx.currentTime);
  osc1.frequency.setValueAtTime(1600, ctx.currentTime + 0.02);
  osc1.frequency.setValueAtTime(400, ctx.currentTime + 0.04);
  osc1.frequency.setValueAtTime(1200, ctx.currentTime + 0.06);
  
  // 失真
  const distortion = ctx.createWaveShaper();
  const curve = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const x = (i / 128) - 1;
    curve[i] = Math.tanh(x * 3);
  }
  distortion.curve = curve;
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.005);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc1.connect(distortion);
  distortion.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
  
  // 低频电流嗡嗡声
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(60, ctx.currentTime);
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.linearRampToValueAtTime(volume * 0.3, ctx.currentTime + 0.01);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration * 0.5);
  
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
}

// ========== 电竞 Esports 音效 ==========

// 电竞悬停音效：技能预备音 - 快速上升滑音，像游戏技能准备就绪
function playEsportsHover(ctx: AudioContext) {
  const duration = 0.08;
  const volume = 0.14;
  
  // 快速上升滑音（技能Ready）
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(300, ctx.currentTime);
  osc1.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + duration * 0.7);
  osc1.frequency.setValueAtTime(900, ctx.currentTime + duration);
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.005);
  gain1.gain.setValueAtTime(volume * 0.8, ctx.currentTime + duration * 0.7);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
  
  // 高频闪光强调
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'triangle';
  osc2.frequency.setValueAtTime(1800, ctx.currentTime + duration * 0.5);
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.setValueAtTime(0, ctx.currentTime + duration * 0.5);
  gain2.gain.linearRampToValueAtTime(volume * 0.5, ctx.currentTime + duration * 0.55);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
}

// 电竞点击音效：Combo确认音 - 双击音 + 低频冲击，像格斗游戏连击提示
function playEsportsClick(ctx: AudioContext) {
  const duration = 0.15;
  const volume = 0.2;
  
  // 第一击
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'square';
  osc1.frequency.setValueAtTime(600, ctx.currentTime);
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.005);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04);
  
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + 0.05);
  
  // 第二击（间隔30ms，音调更高）
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'square';
  osc2.frequency.setValueAtTime(800, ctx.currentTime + 0.03);
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.setValueAtTime(0, ctx.currentTime + 0.03);
  gain2.gain.linearRampToValueAtTime(volume * 1.1, ctx.currentTime + 0.035);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
  
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + 0.1);
  
  // 重低音punch冲击（Hit!）
  const osc3 = ctx.createOscillator();
  const gain3 = ctx.createGain();
  osc3.type = 'sine';
  osc3.frequency.setValueAtTime(80, ctx.currentTime);
  osc3.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.08);
  
  gain3.gain.setValueAtTime(0, ctx.currentTime);
  gain3.gain.linearRampToValueAtTime(volume * 0.8, ctx.currentTime + 0.01);
  gain3.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
  
  osc3.connect(gain3);
  gain3.connect(ctx.destination);
  osc3.start(ctx.currentTime);
  osc3.stop(ctx.currentTime + duration);
  
  // 高频金属音强调
  const osc4 = ctx.createOscillator();
  const gain4 = ctx.createGain();
  osc4.type = 'sawtooth';
  osc4.frequency.setValueAtTime(1200, ctx.currentTime + 0.03);
  
  const filter = ctx.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 800;
  
  gain4.gain.setValueAtTime(0, ctx.currentTime);
  gain4.gain.setValueAtTime(0, ctx.currentTime + 0.03);
  gain4.gain.linearRampToValueAtTime(volume * 0.3, ctx.currentTime + 0.04);
  gain4.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
  
  osc4.connect(filter);
  filter.connect(gain4);
  gain4.connect(ctx.destination);
  osc4.start(ctx.currentTime);
  osc4.stop(ctx.currentTime + duration);
}

// ========== 水墨书卷 Ink 音效 ==========

// 水墨悬停音效：毛笔轻触纸 - 细腻的沙沙声，像笔尖触及宣纸
function playInkHover(ctx: AudioContext) {
  const duration = 0.1;
  const volume = 0.1;
  
  // 滤波白噪声模拟毛笔触纸的沙沙声
  const bufferSize = ctx.sampleRate * duration;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    // 柔和的噪声，快速衰减
    output[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.15));
  }
  
  const noiseNode = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const noiseFilter = ctx.createBiquadFilter();
  
  noiseNode.buffer = noiseBuffer;
  // 带通滤波器限制在2000-4000Hz，模拟纸张摩擦声
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 3000;
  noiseFilter.Q.value = 0.5;
  noiseGain.gain.value = volume;
  
  noiseNode.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noiseNode.start(ctx.currentTime);
  
  // 微弱的低频共鸣（桌面传导）
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(180, ctx.currentTime);
  
  gain.gain.setValueAtTime(0, ctx.currentTime);
  gain.gain.linearRampToValueAtTime(volume * 0.15, ctx.currentTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration * 0.5);
  
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + duration);
}

// 水墨点击音效：墨滴落纸 - 柔和的"啪嗒"声 + 晕染余韵
function playInkClick(ctx: AudioContext) {
  const duration = 0.35;
  const volume = 0.15;
  
  // 墨滴落下的"啪嗒"声（低频脉冲）
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(250, ctx.currentTime);
  osc1.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + 0.05);
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.008);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
  
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + 0.15);
  
  // 墨水渗透的晕染余韵（长尾混响式衰减）
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'triangle';
  osc2.frequency.setValueAtTime(200, ctx.currentTime);
  osc2.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + duration);
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.linearRampToValueAtTime(volume * 0.4, ctx.currentTime + 0.03);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
  
  // 纸张吸收墨水的细微沙沙声
  const bufferSize = ctx.sampleRate * 0.15;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    output[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.3));
  }
  
  const noiseNode = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const noiseFilter = ctx.createBiquadFilter();
  
  noiseNode.buffer = noiseBuffer;
  noiseFilter.type = 'lowpass';
  noiseFilter.frequency.value = 2000;
  noiseGain.gain.value = volume * 0.2;
  
  noiseNode.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noiseNode.start(ctx.currentTime + 0.02);
}

// ========== 青花瓷韵 Qinghua 音效 ==========

// 青花悬停音效：玉佩轻晃 - 清脆的玉石碰撞声，带轻微余韵
function playQinghuaHover(ctx: AudioContext) {
  const duration = 0.18;
  const volume = 0.1;
  
  // 极高频正弦波模拟玉石清脆碰撞
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(3200, ctx.currentTime);
  osc1.frequency.exponentialRampToValueAtTime(2800, ctx.currentTime + duration);
  
  // 高Q值滤波器增强共鸣
  const filter1 = ctx.createBiquadFilter();
  filter1.type = 'peaking';
  filter1.frequency.value = 3000;
  filter1.Q.value = 20;
  filter1.gain.value = 6;
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.002);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc1.connect(filter1);
  filter1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
  
  // 第二块玉轻碰（稍低频率，延迟一点）
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(2600, ctx.currentTime + 0.02);
  osc2.frequency.exponentialRampToValueAtTime(2400, ctx.currentTime + duration);
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.setValueAtTime(0, ctx.currentTime + 0.02);
  gain2.gain.linearRampToValueAtTime(volume * 0.6, ctx.currentTime + 0.025);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
}

// 青花点击音效：瓷碗叩击 - 清亮的"叮"声 + 悠长共鸣
function playQinghuaClick(ctx: AudioContext) {
  const duration = 0.5;
  const volume = 0.14;
  
  // 主音 - 瓷器敲击的清亮"叮"声
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(1400, ctx.currentTime);
  osc1.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + duration);
  
  // 高Q值带通滤波器模拟瓷器共振
  const filter1 = ctx.createBiquadFilter();
  filter1.type = 'bandpass';
  filter1.frequency.value = 1300;
  filter1.Q.value = 30;
  
  gain1.gain.setValueAtTime(0, ctx.currentTime);
  gain1.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.001);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc1.connect(filter1);
  filter1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + duration);
  
  // 二次谐波（2800Hz）
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(2800, ctx.currentTime);
  osc2.frequency.exponentialRampToValueAtTime(2400, ctx.currentTime + duration);
  
  const filter2 = ctx.createBiquadFilter();
  filter2.type = 'bandpass';
  filter2.frequency.value = 2600;
  filter2.Q.value = 35;
  
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.linearRampToValueAtTime(volume * 0.5, ctx.currentTime + 0.001);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration * 0.7);
  
  osc2.connect(filter2);
  filter2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
  
  // 三次谐波（4200Hz）- 瓷器特有的清脆
  const osc3 = ctx.createOscillator();
  const gain3 = ctx.createGain();
  osc3.type = 'sine';
  osc3.frequency.setValueAtTime(4200, ctx.currentTime);
  osc3.frequency.exponentialRampToValueAtTime(3800, ctx.currentTime + duration * 0.5);
  
  gain3.gain.setValueAtTime(0, ctx.currentTime);
  gain3.gain.linearRampToValueAtTime(volume * 0.25, ctx.currentTime + 0.001);
  gain3.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration * 0.4);
  
  osc3.connect(gain3);
  gain3.connect(ctx.destination);
  osc3.start(ctx.currentTime);
  osc3.stop(ctx.currentTime + duration);
  
  // 低频共振（瓷碗整体振动）
  const osc4 = ctx.createOscillator();
  const gain4 = ctx.createGain();
  osc4.type = 'sine';
  osc4.frequency.setValueAtTime(700, ctx.currentTime);
  osc4.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + duration);
  
  gain4.gain.setValueAtTime(0, ctx.currentTime);
  gain4.gain.linearRampToValueAtTime(volume * 0.2, ctx.currentTime + 0.005);
  gain4.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc4.connect(gain4);
  gain4.connect(ctx.destination);
  osc4.start(ctx.currentTime);
  osc4.stop(ctx.currentTime + duration);
}

// ========== 静音状态管理 Hook ==========

/**
 * 音效静音状态管理 Hook
 * 状态持久化到 localStorage
 */
export function useSoundMute() {
  const [isMuted, setIsMuted] = useState(globalMuted);
  
  useEffect(() => {
    const listener = (muted: boolean) => setIsMuted(muted);
    muteListeners.add(listener);
    return () => {
      muteListeners.delete(listener);
    };
  }, []);
  
  const toggleMute = useCallback(() => {
    const newMuted = !globalMuted;
    globalMuted = newMuted;
    localStorage.setItem(SOUND_MUTED_KEY, String(newMuted));
    muteListeners.forEach(listener => listener(newMuted));
  }, []);
  
  const setMuted = useCallback((muted: boolean) => {
    globalMuted = muted;
    localStorage.setItem(SOUND_MUTED_KEY, String(muted));
    muteListeners.forEach(listener => listener(muted));
  }, []);
  
  return {
    isMuted,
    toggleMute,
    setMuted,
  };
}

// ========== 通用音效 Hook ==========

/**
 * 通用主题音效 Hook
 * 支持所有主题
 */
export function useThemeSounds() {
  const { theme } = useTheme();
  const lastHoverTime = useRef(0);
  const [isMuted, setIsMuted] = useState(globalMuted);
  
  // 监听静音状态变化
  useEffect(() => {
    const listener = (muted: boolean) => setIsMuted(muted);
    muteListeners.add(listener);
    return () => {
      muteListeners.delete(listener);
    };
  }, []);
  
  // 悬停音效（带节流，避免过于频繁）
  const playHover = useCallback(() => {
    if (isMuted) return;
    
    const now = Date.now();
    if (now - lastHoverTime.current < 100) return; // 100ms 节流
    lastHoverTime.current = now;
    
    const ctx = getAudioContext();
    if (!ctx) return;
    
    switch (theme) {
      case 'terran':
        playTerranHover(ctx);
        break;
      case 'zerg':
        playZergHover(ctx);
        break;
      case 'protoss':
        playProtossHover(ctx);
        break;
      case 'cyber':
        playCyberHover(ctx);
        break;
      case 'esports':
        playEsportsHover(ctx);
        break;
      case 'ink':
        playInkHover(ctx);
        break;
      case 'qinghua':
        playQinghuaHover(ctx);
        break;
    }
  }, [theme, isMuted]);
  
  // 点击音效
  const playClick = useCallback(() => {
    if (isMuted) return;
    
    const ctx = getAudioContext();
    if (!ctx) return;
    
    switch (theme) {
      case 'terran':
        playTerranClick(ctx);
        break;
      case 'zerg':
        playZergClick(ctx);
        break;
      case 'protoss':
        playProtossClick(ctx);
        break;
      case 'cyber':
        playCyberClick(ctx);
        break;
      case 'esports':
        playEsportsClick(ctx);
        break;
      case 'ink':
        playInkClick(ctx);
        break;
      case 'qinghua':
        playQinghuaClick(ctx);
        break;
    }
  }, [theme, isMuted]);
  
  return {
    playHover,
    playClick,
    isEnabled: !isMuted,
  };
}

/**
 * 星际争霸主题音效 Hook（保留兼容性）
 * @deprecated 请使用 useThemeSounds
 */
export function useStarcraftSounds() {
  return useThemeSounds();
}

// 保留旧的导出名称以保持兼容性
export const useTerranSounds = useThemeSounds;

/**
 * 创建带音效的事件处理器
 */
export function useSoundHandlers() {
  const { playHover, playClick } = useThemeSounds();
  
  const withHoverSound = useCallback(
    <T extends React.MouseEventHandler>(handler?: T) => {
      return ((e: React.MouseEvent) => {
        playHover();
        handler?.(e as React.MouseEvent<never>);
      }) as T;
    },
    [playHover]
  );
  
  const withClickSound = useCallback(
    <T extends React.MouseEventHandler>(handler?: T) => {
      return ((e: React.MouseEvent) => {
        playClick();
        handler?.(e as React.MouseEvent<never>);
      }) as T;
    },
    [playClick]
  );
  
  return {
    playHover,
    playClick,
    withHoverSound,
    withClickSound,
  };
}
